import React, {useEffect, useState} from 'react';
import {Message, MessageData} from "types/Message";
import {Form, Formik, FormikHelpers} from "formik";
import Field from "components/Form/Field";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import FieldHeader from "components/Form/FieldHeader";
import IconName from "types/IconName";
import Select from 'components/Form/Select';
import {Option} from "components/Form/Select/Select";
import {Offer} from 'types/Offer';
import Well from 'components/Well';
import MessageActionType from "../../../types/MessageActionType";
import {ExtendedDurationPicker} from 'components/Form/DurationPicker';
import {Tab} from '@headlessui/react';
import TabButton from 'components/TabButton';
import CriteriaBuilder from 'components/Form/CriteriaBuilder';
import Checkbox from 'components/Checkbox/Checkbox';
import Toggle from 'components/Form/Toggle';
import RecurrenceBuilder from 'components/Form/RecurrenceBuilder';
import FormControls from 'components/FormControls';
import {SingleCombobox} from "../../../components/Form/Form/Control";
import {MessageActionFieldSet} from "../../../components/Form/Form/Fieldset";
import {DateField} from "../../../components/Form/Form/Field";
import Label from "../../../components/Form/Label";
import MessageChannel from "../../../types/MessageChannel";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {TextAreaField} from "../../../components/Form/Form";

// Types ----

enum CriteriaMode {ALL, EXISTING, CUSTOM}

type Props = {
  message?: Message | null
  onSubmit: (values: MessageData, helpers: FormikHelpers<MessageData>) => Promise<any>
}

// Helpers ----

const offerOption = (offer: Offer): Option => ({
  id: offer.id,
  name: offer.name,
  description: (offer.has_expired ? '(expired) ' : '') + offer.incentive,
  icon: IconName.GIFT
})

const offerSource = async (query: string) => {
  return (await ngage.offers.filtered({search: query}))?.data.map(offerOption) || []
}

function getCriteriaMode(message: Message) {
  if (message.has_criteria) return CriteriaMode.CUSTOM;
  if (message.audience_segment) return CriteriaMode.EXISTING;
  return CriteriaMode.ALL;
}

// Component ----

function MessageForm(props: Props) {
  const [criteriaMode, setCriteriaMode] = useState<CriteriaMode>(props.message ? getCriteriaMode(props.message) : CriteriaMode.ALL);
  const [scheduling, setScheduling] = useState<boolean>(!!props.message?.due_at);
  const [selectedOffers, setSelectedOffers] = useState<Array<Option>>(props.message?.offers.map(offerOption) ?? []);

  const {data: audienceSegments} = useQuery(
    ['audience-segments.all', {page: 1}],
    () => ngage.audienceSegments.all(1)
  )

  const selectableSegments = audienceSegments?.data.map(segment => ({
    id: segment.id,
    name: segment.name,
    description: `~${segment.audience_size} people`,
    icon: IconName.USER_GROUP
  })) || []

  const validationSchema = Yup.object().shape({
    channels: Yup.array().min(1, 'Please select at least one channel'),
    due_at: scheduling
      ? Yup.date().required('This field is required')
      : Yup.date().nullable(),
    recurrence: Yup.string().nullable(),
    subject: Yup.string().required('This field is required').max(100, 'Maximum of 100 characters'),
    snippet: Yup.string().when('channels', ([channels]: any, schema: any) => {
      return channels.includes(MessageChannel.PUSH)
        ? schema.required('This field is required').max(100, 'Maximum of 100 characters')
        : schema
    }),
    body: Yup.string().when('channels', ([channels]: any, schema: any) => {
      return channels.includes(MessageChannel.MAIL)
        ? schema.required('This field is required').max(50000, 'Maximum character limit exceeded')
        : schema
    }),
    action: Yup.object().shape({
      type: Yup.string().required('This field is required'),
      text: Yup.string().required('This field is required').max(255, 'Maximum of 255 characters'),
      data: Yup.object(),
    }).nullable(),
    audience_segment_id: criteriaMode === CriteriaMode.EXISTING
      ? Yup.number().required('This field is required')
      : Yup.number().nullable(),
    criteria: criteriaMode === CriteriaMode.CUSTOM
      ? Yup.object().shape({
          groups: Yup.array().of(Yup.object().shape({
            filters: Yup.object().test('at-least-one-filter', 'Choose at least one filter', filters => {
                return Object.keys(filters).length > 0;
              }
            )
          }))
        })
      : Yup.object().nullable(),
    cooldown: Yup.string().required('This field is required'),
  })

  const initialValues: MessageData = {
    subject: props.message?.subject || '',
    snippet: props.message?.snippet || '',
    body: props.message?.body || '',
    offer_ids: props.message?.offers.map(offer => offer.id) || [],
    action: props.message?.action ? {
      type: props.message.action.type || MessageActionType.Unknown,
      text: props.message.action.text || '',
      data: props.message.action.data || {}
    } : null,
    criteria: props.message?.criteria || undefined,
    audience_segment_id: props.message?.audience_segment?.id || undefined,
    cooldown: props.message?.cooldown.toISO() || 'PT1H',
    channels: props.message?.channels || [],
    due_at: props.message?.due_at?.toUTC().toISO() || '',
    recurrence: props.message?.recurrence?.toString() || undefined,
  }

  function toggleAction(enabled: boolean, {setFieldValue}: FormikHelpers<MessageData>): void {
    void setFieldValue('action', enabled ? {
      type: MessageActionType.Overview,
      text: '',
      data: {}
    } : null)
  }

  function selectCriteriaMode(index: number, formik: FormikHelpers<MessageData>): void {
    void formik.setFieldValue('criteria', index === CriteriaMode.CUSTOM ? {groups: [{filters: {}}]} : null)
      .then(_ => {
        void formik.validateField('criteria')
      })
    void formik.setFieldValue('audience_segment_id', null)
      .then(_ => {
        void formik.validateField('audience_segment_id')
      });
    setCriteriaMode(index)
  }

  function toggleScheduling(scheduling: boolean, {setFieldValue}: FormikHelpers<MessageData>): void {
    void setFieldValue('due_at', '');
    void setFieldValue('recurrence', null);
    setScheduling(scheduling)
  }

  // function changeDueAt(event: React.FormEvent<HTMLInputElement>, {setFieldValue}: FormikHelpers<MessageData>): void {
  //   const selectedDateTime = DateTime.fromISO(event.currentTarget.value)
  //   void setFieldValue('due_at', selectedDateTime.toISO())
  // }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />
            
            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Sending
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Choose how and when to send this message
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-6">
                      <fieldset className="space-y-4 md:col-span-6">
                        <legend className="text-base font-medium text-gray-900">Channels *</legend>

                        <Checkbox
                          id="push"
                          name="channels"
                          value="push"
                          label="Push"
                          description="Send as a push notification to each audience member's device"
                        />

                        <Checkbox
                          id="mail"
                          name="channels"
                          value="mail"
                          label="Email"
                          description="Send as an email to each audience member"
                        />

                        {(!!formik.errors.channels && formik.touched.channels) &&
                          <FieldHelp isError>{formik.errors.channels}</FieldHelp>
                        }
                      </fieldset>

                      <fieldset className="space-y-4 md:col-span-6">
                        <legend className="text-base font-medium text-gray-900">Scheduled Sending</legend>

                        <div>
                          <Toggle
                            label="Schedule"
                            checked={scheduling}
                            onChange={checked => toggleScheduling(checked, formik)}
                          />

                          {scheduling &&
                            <div className="pl-14 mt-3">
                              <div className="md:flex items-start">
                                {/*<Field*/}
                                {/*  type="datetime-local"*/}
                                {/*  label="Due At"*/}
                                {/*  name="due_at"*/}
                                {/*  id="due_at"*/}
                                {/*  autoComplete="off"*/}
                                {/*  disabled={!scheduling}*/}
                                {/*  value={formatDateTimeForInput(formik.values.due_at)}*/}
                                {/*  onChange={(event: React.FormEvent<HTMLInputElement>) => changeDueAt(event, formik)}*/}
                                {/*/>*/}

                                <div className='flex flex-col gap-1'>
                                  <Label htmlFor="starts_at">Due At</Label>
                                  <DateField
                                    type='datetime-local'
                                    name="due_at"
                                    id="due_at"
                                    autoComplete="off"
                                    disabled={!scheduling}
                                  />
                                </div>

                                <div className="mt-6 md:mt-0 md:ml-3">
                                  <RecurrenceBuilder
                                    rule={props.message?.recurrence}
                                    onChange={ruleString => formik.setFieldValue('recurrence', ruleString)}
                                  />
                                </div>
                              </div>
                            </div>
                          }
                        </div>
                      </fieldset>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Content
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        The main content of the message
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-6">
                      <div className="md:col-span-4">
                        <Field
                          label="Subject *"
                          hint='100 chars max'
                          type="text"
                          name="subject"
                          id="subject"
                          autoComplete="off"
                          maxLength="100"
                          autoFocus
                        />
                      </div>

                      {formik.values.channels.includes(MessageChannel.PUSH) &&
                        <div className="md:col-span-4">
                          <Field
                            label="Push notification body *"
                            hint="100 chars max"
                            type="text"
                            name="snippet"
                            id="snippet"
                            autoComplete="off"
                            maxLength="100"
                          />
                        </div>
                      }

                      {formik.values.channels.includes(MessageChannel.MAIL) &&
                        <div className="md:col-span-6">
                          <TextAreaField
                            label="Email body *"
                            hint="50,000 chars max"
                            rows={10}
                            name="body"
                            id="body"
                            autoComplete="off"
                            maxChars={50000}
                          />
                        </div>
                      }

                      <div className="md:col-span-3 md:col-start-1">
                        <Select
                          label="Offers"
                          hint="Type to search"
                          source={offerSource}
                          selected={selectedOffers}
                          onChange={offers => {
                            void formik.setFieldValue('offer_ids', offers.map(offer => offer.id));
                            setSelectedOffers(offers);
                          }}
                          multiple
                        />
                      </div>

                      <div className="md:col-span-6">
                        <Toggle
                          label="Action"
                          checked={!!formik.values.action}
                          onChange={checked => toggleAction(checked, formik)}
                        />

                        {!!formik.values.action &&
                          <div className="mt-1">
                            <Well>
                              <MessageActionFieldSet name={'action'} />

                              <div className="mt-4">
                                <Field
                                  label="Text *"
                                  hint='255 chars max'
                                  type="text"
                                  name="action.text"
                                  id="action.text"
                                  autoComplete="off"
                                  maxLength="255"
                                />
                              </div>
                            </Well>
                          </div>
                        }
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Recipients
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Specify who should receive this message
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-6">
                      <div className="md:col-span-6">
                        <Tab.Group selectedIndex={criteriaMode} onChange={index => selectCriteriaMode(index, formik)}>
                          <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                            <TabButton position={TabButton.positions.FIRST}>All</TabButton>
                            <TabButton position={TabButton.positions.MIDDLE}>Existing Segment</TabButton>
                            <TabButton position={TabButton.positions.LAST}>Custom Segment</TabButton>
                          </Tab.List>

                          <Tab.Panels>
                            <Tab.Panel></Tab.Panel>
                            <Tab.Panel>
                              <div className="md:max-w-sm">
                                <SingleCombobox
                                  options={selectableSegments}
                                  selected={selectableSegments.find(segment => segment.id.toString() === formik.values.audience_segment_id?.toString())}
                                  onChange={selected => formik.setFieldValue('audience_segment_id', selected?.id)}
                                />

                                {(!!formik.errors.audience_segment_id && formik.touched.audience_segment_id) &&
                                  <FieldHelp isError>{formik.errors.audience_segment_id}</FieldHelp>
                                }
                              </div>
                            </Tab.Panel>

                            <Tab.Panel>
                              <CriteriaBuilder formik={formik}/>
                            </Tab.Panel>
                          </Tab.Panels>
                        </Tab.Group>
                      </div>

                      <div className="md:col-span-6">
                        <FieldHeader label="Cooldown"/>
                        <div className="mt-1">
                          <ExtendedDurationPicker
                            value={formik.values.cooldown}
                            onChange={duration => formik.setFieldValue('cooldown', duration.toISO())}
                          />

                          {(!!formik.errors?.cooldown && !!formik.touched?.cooldown) &&
                            <FieldHelp isError>
                              {formik.errors.cooldown}
                            </FieldHelp>
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <FormControls formik={formik}/>
            </div>
          </Form>
      )}}
    </Formik>
  );
}

// Export ----

export default MessageForm;